'use client';

import { clsx } from 'clsx';
import { get } from 'lodash';
import { useId } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { trackAnalyticsEvent } from '@/app/lib/analytics';

import { FormFieldMessage } from '../message';

import Checkbox from './checkbox';
import {
  CheckboxVariant,
  CheckboxVerticalVariant,
  type SingularCheckboxProps,
} from './checkbox.types';

import type { FieldValues } from 'react-hook-form';

const SingularCheckbox = <T extends FieldValues>({
  name,
  label,
  description,
  disabled,
  variant = CheckboxVariant.LEFT,
  verticalVariant = CheckboxVerticalVariant.CENTER,
  defaultValue,
  required,
  children,
  analyticsEventName,
}: SingularCheckboxProps<T>) => {
  const id = useId();

  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  const { field } = useController({
    control,
    name,
    disabled,
    defaultValue,
  });

  const error = get(errors, name);

  const hasError = Boolean(error);
  const messageId = `singular-checkbox-message-${id}`;

  return (
    <div
      className={clsx(
        variant === CheckboxVariant.LEFT && 'items-start justify-start',
        'flex flex-col',
        { 'text-error': hasError }
      )}
      data-testid={name}
    >
      <Checkbox
        name={name}
        disabled={field.disabled}
        checked={field.value}
        label={label}
        verticalVariant={verticalVariant}
        variant={variant}
        required={required}
        description={description}
        onChange={() => {
          if (analyticsEventName && !field.value) {
            trackAnalyticsEvent(analyticsEventName);
          }
          field.onChange(!field.value);
        }}
        hasError={hasError}
      />
      {children}
      <FormFieldMessage
        id={messageId}
        error={error?.message as string}
      />
    </div>
  );
};

export default SingularCheckbox;
